import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import FlexBetween from "components/flexbox/FlexBetween";
import { H6 } from "components/Typography";
import { usePageTitle } from "contexts/PageTitleContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import AppPaginationBar from "components/AppPaginationBar";
import buttonConfig from "config/ConfigAccessButton";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "4px 12px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
}));

const Groceries = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  
  // State management
  const [groceries, setGroceries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Pagination state - matching your API response
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    original_text: '',
    translated_text: '',
    source_language: 'en',
    target_language: ''
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // ============ PERMISSION FUNCTIONS ============
  const getCurrentButtonPermissions = () => {
    const moduleConfig = buttonConfig["Translation Management/Content"];
    return moduleConfig ? moduleConfig : {};
  };
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  useEffect(() => {
    setPageTitle('Translation Management');
    fetchGroceries(1, 10);
    fetchSupportedLanguages();
  }, [setPageTitle]);

  const fetchGroceries = async (pageParam = 1, pageSizeParam = 10, searchQuery = null) => {
    setLoading(true);
    setError('');
    
    try {
      let url = `${API_ENDPOINTS.GET_groceries}`;
      const params = new URLSearchParams();
      
      params.append('page', pageParam);
      params.append('page_size', pageSizeParam);
      
      const activeSearch = searchQuery !== null ? searchQuery : searchTerm;
      
      if (activeSearch && activeSearch.trim()) {
        params.append('search', activeSearch.trim());
      }
      
      url += `?${params.toString()}`;
      
      const response = await apiService.get(url);
      
      if (response?.success) {
        const groceriesData = Array.isArray(response.data) ? response.data : [];
        setGroceries(groceriesData);
        setTotalCount(response.count || 0);
        setCurrentPage(response.page || pageParam);
        setPageSize(response.page_size || pageSizeParam);
      } else {
        throw new Error(response?.message || 'Failed to fetch groceries');
      }
    } catch (error) {
      console.error('Error fetching groceries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchGroceries(value, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchGroceries(1, newSize);
  };

  const fetchSupportedLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_supported_languages);
      
      if (response?.success) {
        const languagesData = Array.isArray(response.data) ? response.data : [];
        setSupportedLanguages(languagesData);
      } else {
        console.error('Failed to fetch supported languages:', response?.message);
        setSupportedLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching supported languages:', error);
      setSupportedLanguages([]);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const handleSearch = () => {
    const trimmedSearch = searchInput.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPage(1);
    setHasSearched(true);
    fetchGroceries(1, pageSize, trimmedSearch);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
    fetchGroceries(1, pageSize, "");
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRefresh = () => {
    fetchGroceries(currentPage, pageSize);
  };

  const handleOpenDialog = (grocery = null) => {
    if (grocery) {
      setEditingId(grocery.id);
      setFormData({
        original_text: grocery.original_text || '',
        translated_text: grocery.translated_text || '',
        source_language: grocery.source_language || 'en',
        target_language: grocery.target_language || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        original_text: '',
        translated_text: '',
        source_language: 'en',
        target_language: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      original_text: '',
      translated_text: '',
      source_language: 'en',
      target_language: ''
    });
  };

  const validateForm = () => {
    if (!formData.original_text?.trim() || !formData.translated_text?.trim() || !formData.target_language?.trim()) {
      toast.showError('Please fill all required fields (Original Text, Translated Text, and Target Language)');
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        original_text: formData.original_text.trim(),
        translated_text: formData.translated_text.trim(),
        target_language: formData.target_language.trim(),
        source_language: formData.source_language?.trim() || null
      };

      const response = await apiService.post(API_ENDPOINTS.Add_grocery, payload);

      if (response?.success) {
        toast.showSuccess('Text translation added successfully');
        handleCloseDialog();
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to add text translation');
      }
    } catch (error) {
      console.error('Error adding grocery:', error);
      toast.showError(error?.message || 'Failed to add text translation');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        pk: editingId,
        original_text: formData.original_text.trim(),
        translated_text: formData.translated_text.trim(),
        target_language: formData.target_language.trim(),
        source_language: formData.source_language?.trim() || null
      };

      const response = await apiService.put(
        `${API_ENDPOINTS.Update_grocery}`,
        payload
      );

      if (response?.success) {
        toast.showSuccess('Text translation updated successfully');
        handleCloseDialog();
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to update text translation');
      }
    } catch (error) {
      console.error('Error updating grocery:', error);
      toast.showError(error?.message || 'Failed to update text translation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (grocery) => {
    const confirmed = await showDeleteConfirmation({
      title: 'Delete Text Translation',
      message: `Are you sure you want to delete the translation for "${grocery.original_text}"?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed) return;

    setDeletingId(grocery.id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.Delete_grocery}?id=${grocery.id}`
      );

      if (response?.success) {
        toast.showSuccess('Text translation deleted successfully');
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to delete translation');
      }
    } catch (error) {
      console.error('Error deleting text:', error);
      toast.showError(error?.message || 'Failed to delete translation');
    } finally {
      setDeletingId(null);
    }
  };

  const truncateText = (text, maxChars = 30) => {
    if (!text) return 'N/A';
    const trimmedText = text.trim();
    if (trimmedText.length > maxChars) {
      return trimmedText.substring(0, maxChars) + '...';
    }
    return trimmedText;
  };

  const needsTruncation = (text, maxChars = 30) => {
    if (!text) return false;
    return text.trim().length > maxChars;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && groceries.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading groceries data...</Typography>
      </Box>
    );
  }

  return (
    <Box pt={2} pb={4}>
      {/* Page Header */}
      <FlexBetween mb={3}>
        {/* <Box>
          <H6 fontSize={18} mb={0.5}>Translation Management</H6>
        </Box> */}
        <Box />
      </FlexBetween>

      {/* Search and Filters */}
      <StyledCard sx={{ p: 3, mb: 3 }}>
        <FlexBetween mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Translation Directory
          </Typography>
          <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button> 
        </FlexBetween>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by original text, translated text....."
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                if (!value.trim() && searchTerm.trim()) {
                  handleClearSearch();
                  return;
                }
                setSearchInput(value);
                if (hasSearched && value !== searchTerm) {
                  setHasSearched(false);
                }
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                disabled={!searchInput.trim() || (searchInput === searchTerm && hasSearched)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: "none", 
                  height: 40,
                  boxShadow: 'none',
                }}
              >
                Search
              </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  disabled={!shouldEnableButton('add_translation')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    minWidth: 170,
                    px: 2.5,
                    whiteSpace: 'nowrap',
                    opacity: !shouldEnableButton('add_translation') ? 0.6 : 1
                  }}
                >
                  Add Translation
                </Button>
                
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Rows</InputLabel>
                <Select 
                  value={pageSize} 
                  label="Rows" 
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          maxHeight: 700,
          overflow: 'auto',
          padding: "0px 0px 16px 16px",
          scrollbarWidth: "thin",
          scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
             backgroundColor: theme.palette.grey[200],      
            }}
          >
            <TableRow>
              <StyledTableCell className="header" align="center" sx={{ width: 60, minWidth: 60 }}>
                Sr. No.
              </StyledTableCell>
              <StyledTableCell className="header" sx={{ width: 300 }}>
                Original Text
              </StyledTableCell>
              <StyledTableCell className="header" sx={{ width: 300 }}>
                Translated Text
              </StyledTableCell>
              <StyledTableCell className="header" sx={{ width: 200 }}>
                Source Language
              </StyledTableCell>
              <StyledTableCell className="header" sx={{ width: 260 }}>
                Translated Text Language
              </StyledTableCell>
              <StyledTableCell
                className="header"
                sx={{
                  position: "sticky",
                  right: 0,
                  top: 0,
                  zIndex: 9999,
                  width: 150,
                  boxShadow: "-4px 0 6px rgba(0,0,0,0.15) !important",
                 backgroundColor: theme.palette.grey[200],
                }}
              >
                Actions
              </StyledTableCell>
            </TableRow> 
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <StyledTableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading groceries...
                  </Typography>
                </StyledTableCell>
              </TableRow>
            ) : groceries.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm
                      ? `No groceries found matching "${searchTerm}"`
                      : 'No groceries data available'}
                  </Typography>
                  {searchTerm && (
                    <Button variant="text" onClick={handleClearSearch} sx={{ mt: 1 }}>
                      Clear search
                    </Button>
                  )}
                </StyledTableCell>
              </TableRow>
            ) : (
              groceries.map((grocery, index) => (
                <TableRow
                  key={grocery.id}
                  hover
                  sx={{
                    transition: 'background 0.2s',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <StyledTableCell align="center" sx={{ width: 60, minWidth: 60 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {(currentPage - 1) * pageSize + index + 1}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell sx={{ pl: 2 }}>
                    {needsTruncation(grocery.original_text) ? (
                      <Tooltip title={grocery.original_text || 'N/A'} arrow placement="top">
                        <Typography variant="subtitle2" fontWeight={600} sx={{ cursor: 'help' }}>
                          {truncateText(grocery.original_text)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="subtitle2" fontWeight={600}>
                        {grocery.original_text || 'N/A'}
                      </Typography>
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    {needsTruncation(grocery.translated_text) ? (
                      <Tooltip title={grocery.translated_text || 'N/A'} arrow placement="top">
                        <Typography variant="body2" fontWeight={500} sx={{ cursor: 'help' }}>
                          {truncateText(grocery.translated_text)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" fontWeight={500}>
                        {grocery.translated_text || 'N/A'}
                      </Typography>
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {grocery.source_language_name || 'N/A'}
                    </Typography>
                  </StyledTableCell>

                  <StyledTableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {grocery.target_language_name || 'N/A'}
                    </Typography>
                  </StyledTableCell>

                  <StyledTableCell 
                    sx={{ 
                      position: "sticky",
                      right: 0,
                      zIndex: 4,
                      backgroundColor: theme.palette.background.paper,
                      display: "flex", 
                      gap: 1,
                      padding: "25px 20px"
                    }}
                  >
                    <Box display="flex" gap={1}>
                      <Tooltip title={!shouldEnableButton('edit') ? "You don't have permission to edit" : "Edit"}>
                        <span>
                          <IconButton 
                            size="small" 
                            color="editGreen" 
                            onClick={() => handleOpenDialog(grocery)}
                            disabled={!shouldEnableButton('edit')}
                            sx={{ opacity: !shouldEnableButton('edit') ? 0.5 : 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={!shouldEnableButton('delete') ? "You don't have permission to delete" : "Delete"}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(grocery)}
                            disabled={deletingId === grocery.id || !shouldEnableButton('delete')}
                            sx={{ opacity: !shouldEnableButton('delete') ? 0.5 : 1 }}
                          >
                            {deletingId === grocery.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <AppPaginationBar
        pagination={{
          current_page: currentPage,
          page_size: pageSize,
          total_items: totalCount,
          total_pages: totalPages,
        }}
        onPageChange={(e, page) => handlePageChange(e, page)}
        itemLabel="translations"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingId ? 'Edit Translation Text' : 'Add Translation Text'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small" disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Original Text"
              fullWidth
              value={formData.original_text}
              onChange={(e) => setFormData({ ...formData, original_text: e.target.value })}
              placeholder="Enter original text (e.g., Apple)"
              required
            />
            <TextField
              label="Translated Text"
              fullWidth
              value={formData.translated_text}
              onChange={(e) => setFormData({ ...formData, translated_text: e.target.value })}
              placeholder="Enter translated text"
              required
            />
            <TextField
              label="Source Language"
              fullWidth
              disabled
              value={formData.source_language}
              placeholder="English"
            />
            <FormControl fullWidth required>
              <InputLabel>Translated Text Language</InputLabel>
              <Select
                value={formData.target_language}
                onChange={(e) => setFormData({ ...formData, target_language: e.target.value })}
                label="Translated Text Language"
                disabled={loadingLanguages}
              >
                {loadingLanguages ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Loading languages...
                    </Box>
                  </MenuItem>
                ) : supportedLanguages.length === 0 ? (
                  <MenuItem disabled>No languages available</MenuItem>
                ) : (
                  supportedLanguages.map((language) => (
                    <MenuItem 
                      key={language.code || language.language_code || language.id} 
                      value={language.code || language.language_code || language.name}
                    >
                      {language.name || language.language_name || language.code || language.language_code}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={editingId ? handleUpdate : handleAdd}
            variant="contained"
            disabled={saving || !formData.original_text?.trim() || !formData.translated_text?.trim() || !formData.target_language?.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Groceries;